
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { GameRole, Skill, Challenge, CareerPath } from '../types/game';
import { SpecialEvent } from '../types/specialEvent';
import { useToast } from "@/hooks/use-toast";
import { GameContextType, GameSettings, GameStatistics } from './GameContextTypes';
import { initialGameState } from './initialGameState';
import { gameActions, shouldLevelUp, checkSimulationComplete } from './gameActions';
import { getSpecialEventForYear } from '../data/specialEvents';
import { loadStatistics, saveStatistics } from '../hooks/useStatistics';

const GameContext = createContext<GameContextType | undefined>(undefined);

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Load statistics from localStorage on mount
  const [gameState, setGameState] = useState(() => ({
    ...initialGameState,
    statistics: loadStatistics(),
  }));
  const { toast } = useToast();

  // Save statistics whenever they change
  useEffect(() => {
    saveStatistics(gameState.statistics);
  }, [gameState.statistics]);

  // Check for simulation completion when career level changes
  useEffect(() => {
    const { selectedRole, gameStarted, careerLevel, isSimulationComplete } = gameState;
    
    if (selectedRole && gameStarted) {
      const maxLevelReached = careerLevel === 'Leader' || careerLevel === 'Expert';
      
      if (maxLevelReached && !isSimulationComplete) {
        setGameState(prevState => gameActions.setSimulationComplete(prevState, true));
        
        toast({
          title: "Simulation Complete!",
          description: "Congratulations! You've reached the highest level in your career path.",
          duration: 5000,
        });
        
        setGameState(prevState => ({
          ...prevState,
          achievements: [...prevState.achievements, "Career Simulation Completed"]
        }));
      }
    }
  }, [gameState.careerLevel, gameState.selectedRole, gameState.gameStarted, gameState.isSimulationComplete, toast]);

  // Check for special events when year changes
  useEffect(() => {
    if (gameState.gameStarted && gameState.yearsPassed > 0) {
      const specialEvent = getSpecialEventForYear(gameState.yearsPassed);
      
      if (specialEvent && !gameState.currentSpecialEvent && !gameState.currentChallenge) {
        toast({
          title: "Special Event!",
          description: `${specialEvent.title} - A special industry event requires your attention.`,
          duration: 6000,
        });
        
        setTimeout(() => {
          setGameState(prevState => gameActions.setCurrentSpecialEvent(prevState, specialEvent));
        }, 1000);
      }
    }
  }, [gameState.yearsPassed, gameState.gameStarted, gameState.currentChallenge, gameState.currentSpecialEvent, toast]);

  // Facade methods that update state through actions
  const startGame = (role: GameRole) => {
    setGameState(prevState => {
      const newState = gameActions.startGame(prevState, role);
      // Update statistics when starting a new game
      const roleId = role.id;
      const rolesPlayed = prevState.statistics.rolesPlayed.includes(roleId)
        ? prevState.statistics.rolesPlayed
        : [...prevState.statistics.rolesPlayed, roleId];
      
      return {
        ...newState,
        statistics: {
          ...prevState.statistics,
          sessionsPlayed: prevState.statistics.sessionsPlayed + 1,
          rolesPlayed,
          lastPlayedDate: new Date().toISOString(),
        }
      };
    });
  };

  const makeDecision = (decision: string, outcome: string) => {
    setGameState(prevState => {
      const newState = gameActions.makeDecision(prevState, decision, outcome);
      // Track decision in statistics
      const isSuccessful = outcome.toLowerCase().includes('success') || 
                          outcome.toLowerCase().includes('positive') ||
                          outcome.toLowerCase().includes('improved') ||
                          !outcome.toLowerCase().includes('fail');
      
      return {
        ...newState,
        statistics: {
          ...prevState.statistics,
          totalDecisionsMade: prevState.statistics.totalDecisionsMade + 1,
          successfulDecisions: isSuccessful 
            ? prevState.statistics.successfulDecisions + 1 
            : prevState.statistics.successfulDecisions,
          challengesCompleted: prevState.statistics.challengesCompleted + 1,
        }
      };
    });
  };

  const advanceYear = () => {
    setGameState(prevState => gameActions.advanceYear(prevState));
    
    setGameState(prevState => {
      if (shouldLevelUp(prevState)) {
        return gameActions.levelUp(prevState);
      }
      return prevState;
    });
  };
  
  const updateKpi = (kpi: string, value: number) => {
    setGameState(prevState => gameActions.updateKpi(prevState, kpi, value));
  };
  
  const addSkill = (skill: Skill) => {
    setGameState(prevState => {
      const newState = gameActions.addSkill(prevState, skill);
      return {
        ...newState,
        statistics: {
          ...prevState.statistics,
          skillsUnlocked: newState.skills.length,
        }
      };
    });
  };
  
  const completeChallenge = () => {
    setGameState(prevState => gameActions.completeChallenge(prevState));
  };
  
  const completeSpecialEvent = () => {
    setGameState(prevState => gameActions.completeSpecialEvent(prevState));
  };
  
  const setCurrentChallenge = (challenge: Challenge | null) => {
    setGameState(prevState => gameActions.setCurrentChallenge(prevState, challenge));
  };
  
  const setCurrentSpecialEvent = (event: SpecialEvent | null) => {
    setGameState(prevState => gameActions.setCurrentSpecialEvent(prevState, event));
  };
  
  const setHasTutorialCompleted = (value: boolean) => {
    setGameState(prevState => gameActions.setHasTutorialCompleted(prevState, value));
  };
  
  const levelUp = () => {
    setGameState(prevState => {
      const newState = gameActions.levelUp(prevState);
      
      toast({
        title: `Promotion to ${newState.careerLevel}!`,
        description: `Congratulations on your advancement to ${newState.careerTitle}.`,
        duration: 5000,
      });
      
      // Update highest level in statistics
      const levelOrder = ['Junior', 'Mid-Level', 'Senior', 'Leader', 'Expert'];
      const currentIndex = levelOrder.indexOf(newState.careerLevel);
      const highestIndex = levelOrder.indexOf(prevState.statistics.highestLevel);
      
      return {
        ...newState,
        statistics: {
          ...prevState.statistics,
          highestLevel: currentIndex > highestIndex ? newState.careerLevel : prevState.statistics.highestLevel,
          achievementsEarned: newState.achievements.length,
        }
      };
    });
  };
  
  const selectCareerPath = (path: CareerPath) => {
    setGameState(prevState => {
      const newState = gameActions.selectCareerPath(prevState, path);
      
      const currentCareerLevelData = prevState.selectedRole?.careerPath.find(
        p => p.level === prevState.careerLevel
      );
      
      const pathOption = currentCareerLevelData?.pathOptions?.find(
        option => option.path === path
      );
      
      if (pathOption) {
        toast({
          title: `New Career Path: ${pathOption.title}!`,
          description: pathOption.description,
          duration: 5000,
        });
      }
      
      return newState;
    });
  };

  const updateSettings = (settings: GameSettings) => {
    setGameState(prevState => gameActions.updateSettings(prevState, settings));
    
    toast({
      title: "Settings Updated",
      description: "Your game settings have been saved.",
      duration: 3000,
    });
  };

  const updateStatistics = useCallback((stats: Partial<GameStatistics>) => {
    setGameState(prevState => ({
      ...prevState,
      statistics: {
        ...prevState.statistics,
        ...stats,
      }
    }));
  }, []);

  const contextValue: GameContextType = {
    ...gameState,
    startGame,
    makeDecision,
    advanceYear,
    updateKpi,
    addSkill,
    completeChallenge,
    setCurrentChallenge,
    setCurrentSpecialEvent,
    completeSpecialEvent,
    setHasTutorialCompleted,
    levelUp,
    selectCareerPath,
    updateSettings,
    updateStatistics,
  };

  return (
    <GameContext.Provider value={contextValue}>
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};
